import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { T } from 'gt-react';
import { orpc } from '@/api/rpc/client';
import { CommentsCard } from '@/components/common/comments/CommentsCard';
import { invalidatePricelistCaseQueries } from './pricelistCaseQueryInvalidation';

/**
 * Notes on a pricelist update case.
 *
 * The comments live on the companion task (a pricelist case is not a
 * commentable type of its own), but they are read and written through the
 * CASE's own procedures, which authorize `Subject.Product`. Going through the generic
 * `comments.*` procedures would require `Task:Read`, and granting the pricelist
 * role that would hand it the organization's whole task queue — which the
 * design rejects outright.
 */
export function PricelistCaseComments({
  caseId,
  currentUserId,
  userNameById,
  className,
}: {
  caseId: string;
  currentUserId?: string;
  userNameById?: Map<string, string>;
  className?: string;
}) {
  const queryClient = useQueryClient();
  const commentsQuery = useQuery(
    orpc.inventoryManagement.pricelistCases.listComments.queryOptions({
      input: { caseId },
    }),
  );
  const addComment = useMutation(
    orpc.inventoryManagement.pricelistCases.addComment.mutationOptions(),
  );
  const updateComment = useMutation(
    orpc.inventoryManagement.pricelistCases.updateComment.mutationOptions(),
  );
  const deleteComment = useMutation(
    orpc.inventoryManagement.pricelistCases.deleteComment.mutationOptions(),
  );

  function refreshNotes() {
    invalidatePricelistCaseQueries(queryClient, { comments: true });
  }

  return (
    <CommentsCard
      comments={commentsQuery.data?.comments ?? []}
      isLoading={commentsQuery.isPending}
      isSubmitting={addComment.isPending}
      onSubmit={async (body) => {
        await addComment.mutateAsync({ caseId, body });
        refreshNotes();
      }}
      onUpdate={async (commentId, body) => {
        await updateComment.mutateAsync({ caseId, commentId, body });
        refreshNotes();
      }}
      onDelete={async (commentId) => {
        await deleteComment.mutateAsync({ caseId, commentId });
        refreshNotes();
      }}
      heading={
        <T context="heading for the notes on a pricelist update case">Notes</T>
      }
      currentUserId={currentUserId}
      userNameById={userNameById}
      className={className}
    />
  );
}
